/**
 * Tabla de rejilla.
 *
 * El maquetado construye sus tablas con CSS Grid y una plantilla de columnas distinta por
 * pantalla (`2.2fr 0.7fr 0.9fr 1fr 1fr 1fr 44px`, etc.). Se mantiene esa técnica —cambiarla
 * por `<table>` desalinearía las columnas— y se le añaden los roles ARIA para que un lector de
 * pantalla la siga anunciando como tabla.
 */
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './Icon';
import { useBreakpoint } from '../lib/useBreakpoint';
import styles from './DataTable.module.css';

export interface Column {
  key: string;
  label: string;
  align?: 'left' | 'right';
  /**
   * A partir de qué anchura aparece la columna. Por defecto, siempre.
   *
   * En tablet no caben nueve columnas sin aplastarlas hasta ser ilegibles. Antes se resolvía
   * con `min-width: 760px` y desplazamiento lateral, que es pedirle al usuario que arrastre
   * una tabla por una ventana más estrecha que ella. Ahora la tabla ENSEÑA MENOS y lo que
   * queda se lee entero; el resto sigue estando en la ficha, a un toque.
   */
  desde?: 'tablet' | 'ancho';
}

interface DataTableProps<T> {
  columns: Column[];
  /** Plantilla de columnas de CSS Grid, literal del maquetado. */
  template: string;
  /** Plantilla para las columnas que sobreviven en tablet. Sin ella, se usa `template`. */
  templateTablet?: string;
  rows: T[];
  rowKey: (row: T) => string;
  /** `visibles` dice qué columnas se están pintando: las celdas que sobran no se renderizan. */
  renderRow: (row: T, visibles: Set<string>) => ReactNode;
  /**
   * Cómo se ve una fila en móvil. Cuando se pasa, por debajo de 720px la tabla desaparece y
   * en su lugar va una lista de fichas —otro contenido, no la misma tabla encogida—.
   */
  renderCard?: (row: T) => ReactNode;
  onRowClick?: (row: T) => void;
  /** Cabecera compacta, para la tabla de propiedades. */
  tightHeader?: boolean;
  caption?: string;
}

export function DataTable<T>({
  columns,
  template,
  templateTablet,
  rows,
  rowKey,
  renderRow,
  renderCard,
  onRowClick,
  tightHeader = false,
  caption,
}: DataTableProps<T>) {
  const anchura = useBreakpoint();

  const visibles = columns.filter((column) => {
    if (column.desde === 'ancho') return anchura === 'ancho';
    if (column.desde === 'tablet') return anchura !== 'movil';
    return true;
  });

  const gridStyle: CSSProperties = {
    gridTemplateColumns: anchura === 'ancho' ? template : (templateTablet ?? template),
  };
  const claves = new Set(visibles.map((column) => column.key));

  /*
   * Móvil: fichas. La tabla no se enseña ni oculta por CSS —se sustituye—, para que el lector
   * de pantalla no encuentre la misma lista dos veces.
   */
  if (anchura === 'movil' && renderCard) {
    return (
      <div className={styles.cards} role="list" aria-label={caption}>
        {rows.map((row) => {
          const clickable = Boolean(onRowClick);
          return (
            <div
              key={rowKey(row)}
              role="listitem"
              className={clickable ? `${styles.card} ${styles.cardClickable}` : styles.card}
              onClick={clickable ? () => onRowClick?.(row) : undefined}
              onKeyDown={
                clickable
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onRowClick?.(row);
                      }
                    }
                  : undefined
              }
              tabIndex={clickable ? 0 : undefined}
            >
              {renderCard(row)}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    /**
     * Queda como red de seguridad —una celda con un nombre larguísimo no romperá la página—
     * pero ya no es la estrategia: por debajo de 1100px la tabla quita columnas y por debajo
     * de 720px se convierte en fichas, así que en la práctica no hay nada que arrastrar.
     */
    <div className={styles.scroll}>
    <div role="table" aria-label={caption} className={styles.grid}>
      <div
        role="row"
        className={tightHeader ? `${styles.head} ${styles.headTight}` : styles.head}
        style={gridStyle}
      >
        {visibles.map((column) => (
          <span
            key={column.key}
            role="columnheader"
            className={column.align === 'right' ? styles.right : undefined}
          >
            {column.label}
          </span>
        ))}
      </div>

      {rows.map((row) => {
        const clickable = Boolean(onRowClick);
        return (
          <div
            key={rowKey(row)}
            role="row"
            className={clickable ? `${styles.row} ${styles.rowClickable}` : styles.row}
            style={gridStyle}
            onClick={clickable ? () => onRowClick?.(row) : undefined}
            onKeyDown={
              clickable
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onRowClick?.(row);
                    }
                  }
                : undefined
            }
            tabIndex={clickable ? 0 : undefined}
          >
            {renderRow(row, claves)}
          </div>
        );
      })}
    </div>
    </div>
  );
}

/**
 * Celda de identidad: miniatura o avatar + nombre y línea secundaria.
 * `size="sm"` es la variante del rent roll y la tabla de documentos (13.5px, hueco 12px).
 */
export function IdentityCell({
  visual,
  main,
  sub,
  size = 'default',
}: {
  visual: ReactNode;
  main: string;
  sub?: string | null;
  size?: 'default' | 'sm';
}) {
  const small = size === 'sm';
  return (
    <div className={small ? `${styles.identity} ${styles.identitySm}` : styles.identity}>
      {visual}
      <div>
        <div className={small ? styles.identityMainSm : styles.identityMain}>{main}</div>
        {sub ? <div className={styles.identitySub}>{sub}</div> : null}
      </div>
    </div>
  );
}

export function ChevronCell() {
  return (
    <span className={styles.chevron}>
      <Icon name="chevron-right" size={18} strokeWidth={2.2} />
    </span>
  );
}

export function TableGroupTitle({ title, subtitle }: { title: string; subtitle?: string | null }) {
  return (
    <>
      <div className={styles.groupTitle}>{title}</div>
      {subtitle ? <div className={styles.groupSubtitle}>{subtitle}</div> : null}
    </>
  );
}

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  onChange: (page: number) => void;
  label?: string;
}

/** Pie de paginación. Solo aparece si hay más de una página. */
export function TablePagination({ page, totalPages, total, onChange, label = 'registros' }: PaginationProps) {
  if (totalPages <= 1) return null;
  return (
    <div className={styles.footer}>
      <span>
        {total} {label} · página {page} de {totalPages}
      </span>
      <div className={styles.pager}>
        <button
          type="button"
          className={styles.pagerButton}
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
        >
          Anterior
        </button>
        <button
          type="button"
          className={styles.pagerButton}
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
        >
          Siguiente
        </button>
      </div>
    </div>
  );
}

export const cellStyles = styles;
